import {gql} from '@apollo/client';

export const GET_APP_CONFIGURATION = gql`
  query GetAppConfig {
    getAppConfig {
      ... on Error {
        message
        statusCode
      }
      ... on AppConfigType {
        config {
          regionAcl {
            region
            # Read so the editor can put it back. updateAppConfig replaces the
            # whole regionAcl array, so an entry saved without its products
            # would lose them — the same trap rules has below.
            products
            # Selected so the editor can put it back: updateAppConfig
            # replaces the whole regionAcl array, so an entry saved without
            # its rules loses them. Reading them is what preserves them.
            rules {
              assetClassKeys
              instrumentKeys
              operation
              exchanges
            }
            message {
              firstTimeUser {
                title
                language
                description
              }
              existingUsers {
                title
                language
                description
              }
            }
            cutoffDate
            acl
          }
          referralChannels {
            icon
            channel {
              FR
              EN
            }
          }
          dabaSubscription {
            proPlanPrices {
              amount
              currency
              subscriptionType
            }
            academyPlanPrices {
              amount
              currency
            }
          }
          withdrawalReasons
          whatWouldYouUseDabaFor
          documentSettings {
            combineStatement
            sendToUserAutomatically
            statementPeriods {
              monthly
              annually {
                day
                month
              }
            }
          }
          inAppReviewSettings {
            isEnabled
            frequencyControl {
              maxPrompts
              daysInterval
              cooldownPeriod
            }
            filters {
              hasMadeInvestment
            }
          }
          recurringInvestmentSettings {
            scheduledStartTime
            scheduledEndTime
            maxRetries
            maxFailedInvestmentCycles
          }
          inventoryPredictionSettings {
            highPriorityThresholds {
              currencyCode
              amount
            }
            financeTeamEmail
          }
          discountSettings {
            pro
            retry
          }
          paymentMethodSettings {
            paymentMethod
            providerName
            tagName
            tutorialLink
          }
          versionInfo {
            isCritical
            android
            ios
            latestVersion
            isInReview
            message
          }
          securitySettings {
            admin2FA
          }
          dabaWhatsappNumber
          brvmVideoId
          platformReviewSettings {
            filters {
              hasCompletedProfile
              hasInvestedInAnyCampaign
            }
            reviewPeriodEnd
          }
          totalEstimatedRewardsValue {
            amount
            currency
          }
          salesAgentHub {
            prospectEligibilityDaysSincePrimaryAgentInteraction
          }
          aiSupport {
            maxActiveTickets
            maxUserMessageCharacters
            maxActiveAnonymousTickets
            abandonAfterMinutes
            firstResponseTargetSeconds
            agentAvailability {
              timezone
              weeklyHours {
                dayOfWeek
                startTime
                endTime
              }
            }
          }
          notificationOrchestrator {
            enabled
            governanceEnabled
            defaultApprovers
          }
        }
      }
    }
  }
`;

// Isolated from GET_APP_CONFIGURATION on purpose. That query runs with
// errorPolicy 'none', so a single field the gateway has not published yet
// blanks the whole Mobile Settings page. Keeping the KYC selection here means
// a schema mismatch can only take down the KYC card.
export const GET_KYC_APP_CONFIGURATION = gql`
  query GetKycAppConfig {
    getAppConfig {
      ... on Error {
        message
        statusCode
      }
      ... on AppConfigType {
        config {
          kyc {
            nationalIdentityNumber
            socialInsuranceNumber
            ssn
            idcard
            passport
            license
            # Read so KycConfig can send it back untouched: a backend that
            # replaces rather than merges the kyc object would drop it.
            blacklistedRegions
            # Which path the app takes to submit documents: the Smile SDK, the
            # backend-managed route with the quality gate, or manual review.
            submissionRoute
            # The pre-flight quality gate on the backend-managed route.
            documentValidation {
              enabled
              mode
            }
            manualFallback {
              enabled
              selfieRequired
              autoSubmitToSmile {
                enabled
                maxAttempts
                retryAfterMinutes
                maxPerRun
              }
            }
            aml {
              onDocumentCheck
              deferInAfrica
            }
          }
        }
      }
    }
  }
`;

// Isolated from GET_APP_CONFIGURATION for the same reason the KYC selection
// above is: `disclosures` lands at the gateway only once the users-service
// schema is republished, and GET_APP_CONFIGURATION runs with errorPolicy
// 'none' — selecting an unpublished field there would blank every Platform
// Settings tab, not just this one.
//
// Every field of every entry is selected because updateAppConfig replaces the
// whole `disclosures` array. The editor writes the entire list back on save,
// so anything it does not read here is what it would silently drop: another
// surface's entry, or another language's title/body.
export const GET_DISCLOSURES_APP_CONFIGURATION = gql`
  query GetDisclosuresAppConfig {
    getAppConfig {
      ... on Error {
        message
        statusCode
      }
      ... on AppConfigType {
        config {
          disclosures {
            surface
            version
            active
            mandatory
            provider
            translations {
              language
              title
              body
            }
          }
        }
      }
    }
  }
`;

// The disclosure editor renders one tab per language rather than a hardcoded
// EN/FR pair, so a language added to the API enum needs no change here. Asked
// separately (and tolerantly) because introspection can be switched off at the
// gateway — the editor falls back to the supportedLanguages constant.
export const GET_DISCLOSURE_LANGUAGES = gql`
  query DisclosureLanguages {
    __type(name: "Languages") {
      enumValues {
        name
      }
    }
  }
`;

export const UPDATE_APP_CONFIGURATION = gql`
  mutation UpdateAppConfig($data: UpdateAppConfigInput) {
    updateAppConfig(data: $data) {
      ... on Error {
        message
        statusCode
      }
      ... on AppConfigType {
        config {
          versionInfo {
            latestVersion
          }
        }
      }
    }
  }
`;

// The support team's first-response target. Sent on its own so the existing
// aiSupport merge keeps every other knob as it is.
export const SET_SUPPORT_RESPONSE_TARGET = gql`
  mutation SetSupportResponseTarget($seconds: Int!) {
    updateAppConfig(data: {aiSupport: {firstResponseTargetSeconds: $seconds}}) {
      __typename
      ... on Error {
        message
        statusCode
      }
      ... on AppConfigType {
        config {
          aiSupport {
            firstResponseTargetSeconds
          }
        }
      }
    }
  }
`;

// The whole weekly schedule is replaced on save; null clears it.
export const SET_SUPPORT_AVAILABILITY = gql`
  mutation SetSupportAvailability(
    $availability: AiSupportAgentAvailabilityInput
  ) {
    updateAppConfig(data: {aiSupport: {agentAvailability: $availability}}) {
      __typename
      ... on Error {
        message
        statusCode
      }
      ... on AppConfigType {
        config {
          aiSupport {
            agentAvailability {
              timezone
              weeklyHours {
                dayOfWeek
                startTime
                endTime
              }
            }
          }
        }
      }
    }
  }
`;

export const FORCE_SIGN_OUT = gql`
  mutation ForceSignOutUsers($forceSignOut: Boolean!) {
    forceSignOutUsers(forceSignOut: $forceSignOut) {
      ... on Error {
        message
        statusCode
      }
    }
  }
`;

export const CHANGE_ALL_USER_DOCUMENT_STATUS = gql`
  mutation ChangeAllUserDocumentStatus(
    $filter: ChangeAllUserDocumentStatusFilter!
    $active: Boolean
  ) {
    changeAllUserDocumentStatus(filter: $filter, active: $active) {
      ... on Error {
        message
        statusCode
      }
      ... on ResponseWithUserDocument {
        message
      }
    }
  }
`;

export const UPDATE_MAINTENANCE_MODE = gql`
  mutation SetMaintenanceMode($data: MaintenanceModeInput!) {
    setMaintenanceMode(data: $data) {
      ... on Error {
        message
        statusCode
      }
      ... on Response {
        message
      }
    }
  }
`;

export const GET_MAINTENANCE_MODE = gql`
  query GetMaintenanceMode {
    getMaintenanceMode {
      ... on Error {
        message
        statusCode
      }
      ... on ResponseWithAnything {
        data
        message
      }
    }
  }
`;

export const GET_PHONE_NUMBER_EXAMPLES = gql`
  query GetPhoneExamples($filter: PhoneExampleFilter!) {
    getPhoneExamples(filter: $filter) {
      ... on Error {
        statusCode
        message
      }
      ... on ResponseWithPhoneNumberExamples {
        data {
          id
          countryCode
          countryName
          numberExample
          createdAt
        }
        pagination {
          total
        }
      }
    }
  }
`;

export const ADD_PHONE_NUMBER_EXAMPLES = gql`
  mutation AddMultiplePhoneExample($inputs: [PhoneExampleInput!]) {
    addMultiplePhoneExample(inputs: $inputs) {
      ... on Error {
        statusCode
        message
      }
      ... on ResponseWithPhoneNumberExamples {
        message
      }
    }
  }
`;

export const DELETE_PHONE_NUMBER_EXAMPLE = gql`
  mutation DeletePhoneExampleById($deletePhoneExampleByIdId: ID!) {
    deletePhoneExampleById(id: $deletePhoneExampleByIdId) {
      ... on Error {
        message
        statusCode
      }
      ... on ResponseWithPhoneNumberExample {
        message
      }
    }
  }
`;

export const UPDATE_PHONE_NUMBER_EXAMPLE = gql`
  mutation UpdatePhoneExample(
    $updatePhoneExampleId: ID!
    $input: PhoneExampleInput!
  ) {
    updatePhoneExample(id: $updatePhoneExampleId, input: $input) {
      ... on Error {
        statusCode
        message
      }
      ... on ResponseWithPhoneNumberExample {
        message
      }
    }
  }
`;
